// @ts-strict;
import type { IntervalName } from 'shared/definitions/musicTheory/intervals';
import Interval from '../uTheory.Notation/interval';
import { randomIntervalQuestions } from './randomIntervalGenerators';

export const ascendingMelodicIntervalSkills = [
  'asc_m2_et',
  'asc_M2_et',
  'asc_m3_et',
  'asc_M3_et',
  'asc_P4_et',
  'asc_TT_et',
  'asc_P5_et',
  'asc_m6_et',
  'asc_M6_et',
  'asc_m7_et',
  'asc_M7_et',
  'asc_P8_et',
] as const;

type AscendingMelodicIntervalSkill = typeof ascendingMelodicIntervalSkills[number];

export function isAscendingMelodicIntervalSkill(
  skill: string
): skill is typeof ascendingMelodicIntervalSkills[number] {
  return ascendingMelodicIntervalSkills.includes(skill as AscendingMelodicIntervalSkill);
}

export const descendingMelodicIntervalSkills = [
  'dsc_m2_et',
  'dsc_M2_et',
  'dsc_m3_et',
  'dsc_M3_et',
  'dsc_P4_et',
  'dsc_TT_et',
  'dsc_P5_et',
  'dsc_m6_et',
  'dsc_M6_et',
  'dsc_m7_et',
  'dsc_M7_et',
  'dsc_P8_et',
] as const;

type DescendingMelodicIntervalSkill = typeof descendingMelodicIntervalSkills[number];

export function isDescendingMelodicIntervalSkill(
  skill: string
): skill is typeof descendingMelodicIntervalSkills[number] {
  return descendingMelodicIntervalSkills.includes(skill as DescendingMelodicIntervalSkill);
}

export const harmonicIntervalSkills = [
  'm2_harmonic',
  'M2_harmonic',
  'm3_harmonic',
  'M3_harmonic',
  'P4_harmonic',
  'TT_harmonic',
  'P5_harmonic',
  'm6_harmonic',
  'M6_harmonic',
  'm7_harmonic',
  'M7_harmonic',
  'P8_harmonic',
] as const;

type HarmonicIntervalSkill = typeof harmonicIntervalSkills[number];

export function isHarmonicIntervalSkill(
  skill: string
): skill is typeof harmonicIntervalSkills[number] {
  return harmonicIntervalSkills.includes(skill as HarmonicIntervalSkill);
}

const compoundIntervalSkills = ['asc_compound_et', 'dsc_compound_et', 'compound_harmonic'] as const;

type CompoundIntervalSkill = typeof compoundIntervalSkills[number];

export function isCompoundIntervalSkill(
  skill: string
): skill is typeof compoundIntervalSkills[number] {
  return compoundIntervalSkills.includes(skill as CompoundIntervalSkill);
}

const intervalsSkills = [
  ...ascendingMelodicIntervalSkills,
  ...descendingMelodicIntervalSkills,
  ...harmonicIntervalSkills,
  ...compoundIntervalSkills,
] as const;

export type IntervalEarTrainingSkill = typeof intervalsSkills[number];

const compoundIntervals = [
  'm9',
  'M9',
  'm10',
  'M10',
  'P11',
  'd12',
  'P12',
  'm13',
  'M13',
  'm14',
  'M14',
  'P15',
];
const skillIdToIntervalNamesMap: Record<
  IntervalEarTrainingSkill,
  [IntervalName, 'harmonic' | 'melodic'][]
> = {
  asc_m2_et: [['m2', 'melodic']],
  asc_M2_et: [['M2', 'melodic']],
  asc_m3_et: [['m3', 'melodic']],
  asc_M3_et: [['M3', 'melodic']],
  asc_P4_et: [['P4', 'melodic']],
  asc_TT_et: [['d5', 'melodic']],
  asc_P5_et: [['P5', 'melodic']],
  asc_m6_et: [['m6', 'melodic']],
  asc_M6_et: [['M6', 'melodic']],
  asc_m7_et: [['m7', 'melodic']],
  asc_M7_et: [['M7', 'melodic']],
  asc_P8_et: [['P8', 'melodic']],
  asc_compound_et: compoundIntervals.map((i) => [i as IntervalName, 'melodic']),
  dsc_m2_et: [['-m2', 'melodic']],
  dsc_M2_et: [['-M2', 'melodic']],
  dsc_m3_et: [['-m3', 'melodic']],
  dsc_M3_et: [['-M3', 'melodic']],
  dsc_P4_et: [['-P4', 'melodic']],
  dsc_TT_et: [['-d5', 'melodic']],
  dsc_P5_et: [['-P5', 'melodic']],
  dsc_m6_et: [['-m6', 'melodic']],
  dsc_M6_et: [['-M6', 'melodic']],
  dsc_m7_et: [['-m7', 'melodic']],
  dsc_M7_et: [['-M7', 'melodic']],
  dsc_P8_et: [['-P8', 'melodic']],
  dsc_compound_et: compoundIntervals.map((i) => [`-${i}` as IntervalName, 'melodic']),
  m2_harmonic: [['m2', 'harmonic']],
  M2_harmonic: [['M2', 'harmonic']],
  m3_harmonic: [['m3', 'harmonic']],
  M3_harmonic: [['M3', 'harmonic']],
  P4_harmonic: [['P4', 'harmonic']],
  TT_harmonic: [['d5', 'harmonic']],
  P5_harmonic: [['P5', 'harmonic']],
  m6_harmonic: [['m6', 'harmonic']],
  M6_harmonic: [['M6', 'harmonic']],
  m7_harmonic: [['m7', 'harmonic']],
  M7_harmonic: [['M7', 'harmonic']],
  P8_harmonic: [['P8', 'harmonic']],
  compound_harmonic: compoundIntervals.map((i) => [i as IntervalName, 'harmonic']),
};

export function generateIntervalETQuestions(
  skillIds: IntervalEarTrainingSkill[],
  numberOfQuestions: number
) {
  const intervals = skillIds.map((id) => skillIdToIntervalNamesMap[id]).flat();
  const intervalStrings = intervals.map(
    ([intervalName, harmonicOrMelodic]) => `${intervalName}/${harmonicOrMelodic}`
  );
  console.log({ skillIds, intervals });
  // If we have 4 or more intervals, lets reject duplicates on the interval type
  // so we get a wide variety of intervals.
  // Otherwise, reject enharmonically.
  const distinctIntervals = intervals
    .map(([intervalName]) => intervalName.replace('-', ''))
    .filter((i): i is IntervalName => !!i)
    .reduce((accum: IntervalName[], i) => {
      if (!accum.includes(i)) accum.push(i);
      return accum;
    }, []).length;
  const comparisonType = distinctIntervals >= 4 ? 'interval' : 'enharmonic';

  return randomIntervalQuestions(numberOfQuestions, intervalStrings, { comparisonType });
}

export function getNearbyIntervalETSkills(
  skillId: IntervalEarTrainingSkill,
  availableSkills: IntervalEarTrainingSkill[]
): IntervalEarTrainingSkill[] {
  if (isAscendingMelodicIntervalSkill(skillId)) {
    let potentialSkillsToTrain = ascendingMelodicIntervalSkills.filter(
      (s) => availableSkills.includes(s) || s === skillId
    );
    if (potentialSkillsToTrain.length < 3) {
      potentialSkillsToTrain = ascendingMelodicIntervalSkills.slice(0);
    }
    let index = potentialSkillsToTrain.indexOf(skillId);
    index = Math.max(index - 2, 0);
    return potentialSkillsToTrain.slice(index, index + 4);
  }
  if (isDescendingMelodicIntervalSkill(skillId)) {
    let potentialSkillsToTrain = descendingMelodicIntervalSkills.filter(
      (s) => availableSkills.includes(s) || s === skillId
    );
    if (potentialSkillsToTrain.length < 3) {
      potentialSkillsToTrain = descendingMelodicIntervalSkills.slice(0);
    }
    let index = potentialSkillsToTrain.indexOf(skillId);
    index = Math.max(index - 2, 0);
    return potentialSkillsToTrain.slice(index, index + 4);
  }

  if (isHarmonicIntervalSkill(skillId)) {
    let potentialSkillsToTrain = harmonicIntervalSkills.filter(
      (s) => availableSkills.includes(s) || s === skillId
    );
    if (potentialSkillsToTrain.length < 3) potentialSkillsToTrain = harmonicIntervalSkills.slice(0);
    let index = potentialSkillsToTrain.indexOf(skillId);
    index = Math.max(index - 2, 0);
    return potentialSkillsToTrain.slice(index, index + 4);
  }
  throw Error(`${skillId} is not a valid interval ear training skill.`);
}

/**
 * @param {IntervalQuestion} intervalQuestion,
 * @returns {string[]} skills
 */
export function intervalToETSkills({
  interval,
  harmonic,
}: {
  interval: Interval;
  harmonic: boolean;
}): IntervalEarTrainingSkill {
  const matchingSkill = Object.entries(skillIdToIntervalNamesMap).find(([, skillIntervals]) =>
    skillIntervals.find(([intervalString, harmonicOrMelodic]) => {
      const intervalB = new Interval(intervalString);
      // This fun is necessary to catch enharmonic spellings of the intervals listed in skills!
      return (
        interval.direction === intervalB.direction &&
        interval.getSemiTones() === intervalB.getSemiTones() &&
        harmonicOrMelodic === (harmonic ? 'harmonic' : 'melodic')
      );
    })
  );
  if (!matchingSkill) {
    throw new Error(
      `No matching ET skill found for ${interval} ${harmonic ? 'harmonic' : 'melodic'}`
    );
  }

  return matchingSkill[0] as IntervalEarTrainingSkill;
}
